<script setup lang="ts">
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import { transformShortTitle } from '~/utils/solr/law-utils'

const props = defineProps<{ node: LawAndCommentary, isSelected?: boolean, isScrollTop?: boolean }>()

const lawNodeOpen = ref<boolean>(false)
const { textLabel } = useLawTextLabels()

watch(() => props.isScrollTop, (newVal) => {
  if (newVal) {
    lawNodeOpen.value = false
  }
})
</script>

<template>
  <div v-if="isScrollTop" class="flex flex-col gap-xs min-w-0">
    <LawTitle
      :node-type="node.nodeType"
      :title="node.title"
      :short-title="node.shortTitle ?? node.title"
      :title-html="node.titleHtml"
    />
    <ReadingMetadata :node="node" orientation="vertical" />
    <span class="text-caption italic">
      Kilde: {{ node.source }}
    </span>
  </div>
  <details
    v-else
    :open="lawNodeOpen"
    class="top-m flex flex-col justify-between @sm/content:self-center"
    :class="{
      'bg-surface-hover dark:bg-transparent': isSelected,
      'bg-white dark:bg-transparent': !isSelected,
    }"
    @toggle="lawNodeOpen = !lawNodeOpen"
  >
    <summary class="flex gap-2xs cursor-pointer items-center">
      <DetailsIcon
        :open="lawNodeOpen"
        :class="{
          'bg-blurple-50 dark:bg-carbon-400': isSelected,
        }"
      />
      <h2 class="title-xs text-primary font-bold">
        <span class="sr-only">{{ textLabel }} til </span>{{ transformShortTitle(node.shortTitle).title ?? node.title }}
      </h2>
    </summary>
    <div class="flex flex-col pt-xs gap-xs" :class="{ 'animate-slide-down': lawNodeOpen }">
      <ReadingMetadata :node="node" orientation="vertical" class="pl-l" />
      <span :aria-label="`Kilde: ${node.source}`" class="text-caption text-secondary italic">
        Kilde: {{ node.source }}
      </span>
    </div>
  </details>
</template>
